import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, EMPTY, filter, from, map, mergeMap, Observable, of, take, tap } from "rxjs";
import { LoaderStep, makeErrorReadable, SnackbarService, UxValueService } from "design-system";
import { HttpClient } from "@angular/common/http";
import { SignaloidWrapperService } from "../api-services/signaloid-wrapper.service";
import { WsManagerService } from "../api-services/ws-manager.service";
import { DemoApplicationCard, DemoRepositoriesMap } from "../../models/demo.applications";
import { AVAILABLE_CHANNEL_PREFIXES, PUBLIC_CHANNEL_PREFIXES } from "../../models/ws-manager.models";
import { Router } from "@angular/router";
import { BuildDetails } from "@signaloid/cloud-compute-engine-client";
import { PosthogService } from "../monitoring/posthog.service";

@Injectable({
	providedIn: "root",
})
export class ApiDemoExecutionService {
	public state: BehaviorSubject<string> = new BehaviorSubject("idle");
	private demoObject: DemoApplicationCard | undefined;
	private repository: any;
	private arguments: string = "";
	private buildId: string = "";
	private argumentsMap: { [key in string]: string } = {};
	private defaultArguments = "";
	private resampleToSize = 64;
	public authorizationFree = false;

	constructor(
		private signaloidWrapperService: SignaloidWrapperService,
		private uxValueService: UxValueService,
		private wsManagerService: WsManagerService,
		private http: HttpClient,
		private snackbarService: SnackbarService,
		private router: Router,
		private posthog: PosthogService,
	) {}

	public setAuthorizationFree(value: boolean) {
		this.authorizationFree = value;
	}

	public async setDemoObject(demoObject: DemoApplicationCard) {
		this.demoObject = demoObject;
		if (this.authorizationFree && demoObject.authFreeBuildId) this.buildId = demoObject.authFreeBuildId;

		if (demoObject.repoUrl) this.repository = DemoRepositoriesMap[demoObject.repoUrl];
		else {
			console.error("No repository found for demo");
			this.state.next("connectRepo:error");
		}
	}

	public setArgumentsFromString(input: string) {
		this.defaultArguments = input;
	}

	public getBuildsFor(repoId: string) {
		return this.signaloidWrapperService.getBuildsFor(repoId).pipe(take(1));
	}

	public getLatestBuild(repoId: string | undefined) {
		if (this.authorizationFree) {
			if (this.demoObject?.authFreeBuildId) {
				return of({
					BuildID: this.demoObject.authFreeBuildId,
				});
			} else {
				console.warn("auth free build id is set");
				return EMPTY;
			}
		}
		if (!repoId) return EMPTY;
		return this.signaloidWrapperService.getBuildsFor(repoId).pipe(
			take(1),
			map((res) => res.Builds.filter((b: any) => b.Status === "Completed")),
			map((res: BuildDetails[]) => {
				return res.sort((a, b) => {
					if (a.UpdatedAt && b.UpdatedAt) return b.UpdatedAt - a.UpdatedAt;
					return 0;
				});
			}),
			map((res: BuildDetails[]) =>
				res.filter((elem) => elem.Application.Repository.Core === this.demoObject?.coreId),
			),
			map((res: BuildDetails[]) => {
				if (res.length === 0) {
					return undefined;
				}
				if (res[0].Status !== "Completed") {
					this.posthog.capture("build_not_completed");
				}
				return res[0];
			}),
			tap((res: any) => {
				console.log({ res });
				this.buildId = res?.BuildID;
			}),
			catchError((err) => {
				this.posthog.captureError(err);
				return of(undefined);
			}),
		);
	}

	public setArgumentsForSingleDistribution(
		input: { distribution: [number, number][]; value: number },
		flags: string,
	) {
		this.arguments = flags;
		this.arguments += this.uxValueService.buildDistributionArgs(
			{
				initialDistribution: input.distribution,
				initialValue: input.value,
			},
			this.resampleToSize,
		);
	}

	public setArgumentsForMultipleDistributions(
		input: { distribution?: [number, number][]; value: number | string },
		argument: string,
		scale?: number,
	) {
		if (input?.distribution) {
			try {
				const uxValue = this.uxValueService.buildDistributionArgs(
					{
						initialDistribution: input.distribution,
						initialValue: input.value as number,
						scale,
					},
					this.resampleToSize,
				);
				this.argumentsMap[argument] = uxValue;
			} catch (error) {
				this.snackbarService.openSnackbar({
					header: "Error",
					type: "error",
					description: "Invalid distribution, you need at least one non-zero value",
				});
				console.log(error);
			}
		} else {
			this.argumentsMap[argument] = `${input?.value}`;
		}
		this.arguments = this.defaultArguments;
		Object.keys(this.argumentsMap).forEach((key) => {
			this.arguments += `${key} ${this.argumentsMap[key]}`;
		});
	}

	public removeArgumentFromMap(argument: string) {
		delete this.argumentsMap[argument];
		this.arguments = this.defaultArguments;
		Object.keys(this.argumentsMap).forEach((key) => {
			this.arguments += `${key} ${this.argumentsMap[key]}`;
		});
	}

	public executeDemo(includeSignaloidStorage: boolean = false) {
		let taskId = "";
		if (!this.arguments) this.arguments = this.defaultArguments;
		if (this.buildId) {
			this.state.next("startTask");
			if (this.authorizationFree) {
				return this.getTaskObservableWithNoAuthorization();
			}
			console.log({ includeSignaloidStorage });
			return this.getTaskObservable(taskId);
		} else if (this.repository && this.demoObject?.coreId && this.repository?.RepositoryID) {
			return this.subscribeToBuildChannelWs().pipe(
				mergeMap((subscriptionId) => {
					this.state.next("building_repo");
					return this.getBuildingObservable(subscriptionId, taskId);
				}),
			);
		} else {
			console.warn(
				"One of them is missing",
				this.repository,
				this.demoObject?.coreId,
				this.repository?.RepositoryID,
			);
			this.state.next("connectRepo:error");
			return of(Error("No repository or demo application found"));
		}
	}

	private getBuildingObservable(subscriptionId: string, taskId: string) {
		if (!this.demoObject?.coreId) {
			throw new Error("No core id found");
		}
		return this.signaloidWrapperService
			.buildRepository(this.repository?.RepositoryID, this.demoObject?.coreId)
			.pipe(
				mergeMap((buildResponse: any) => {
					return this.wsManagerService.getListener(subscriptionId);
				}),
				filter((res) => res && res.data),
				map((res) => res.data),
				filter(
					(buildResponse: any) => buildResponse.status === "Completed" || buildResponse.status === "Stopped",
				),
				tap((res) => (this.buildId = res["buildId"])),
				mergeMap((buildResponse: any) => {
					if (buildResponse.status === "Stopped") {
						this.state.next("building_repo:error");
						console.log(buildResponse);
						return of(Error("Build stopped"));
					} else {
						this.state.next("startTask");
						return this.getTaskObservable(taskId);
					}
				}),
				tap((taskResponse: any) => {
					if (taskResponse.status && taskResponse.status === "Completed") {
						this.state.next("idle");
					}
				}),
				catchError((err) => {
					console.error(err);
					return EMPTY;
				}),
			);
	}

	private getTaskObservable(taskId: string) {
		return this.subscribeToTaskChannelWs(taskId).pipe(
			mergeMap((subscriptionId) => {
				this.state.next("runTask");
				return this.signaloidWrapperService
					.startTask(this.buildId, this.arguments, this.authorizationFree)
					.pipe(
						tap((res) => (taskId = res.TaskID)),
						mergeMap((taskResponse) => {
							return this.wsManagerService.getListener(subscriptionId);
						}),
						filter((res) => res && res.data),
						map((res) => res.data),
						filter((res) => res.taskId === taskId),
						filter(
							(taskResponse) => taskResponse.status === "Completed" || taskResponse.status === "Stopped",
						),
						mergeMap((taskResponse: any) => {
							if (taskResponse.status === "Stopped") {
								this.state.next("runTask:error");
								throw Error("Task stopped");
							} else {
								this.state.next("fetchOutputs");
								return from(this.signaloidWrapperService.getTaskOutputs(taskResponse.taskId));
							}
						}),
						tap((taskResponse: any) => {
							if (taskResponse.status && taskResponse.status === "Completed") {
								this.state.next("idle");
							}
						}),
						catchError((err) => {
							this.posthog.captureError(err);
							this.snackbarService.openSnackbar({
								header: "Task stopped",
								type: "error",
								description: makeErrorReadable(err),
							});
							if (err?.status === 401 || err?.message === "Failed to retrieve ID token") {
								this.router.navigate(["/login"]);
							}
							console.error(err);
							this.posthog.captureError(err);
							return of(Error("Task stopped"));
						}),
					);
			}),
		);
	}

	public getTaskObservableWithNoAuthorization() {
		let taskId = "";
		return this.signaloidWrapperService.startTask(this.buildId, this.arguments, this.authorizationFree).pipe(
			tap((res) => (taskId = res.TaskID)),
			mergeMap((taskResponse) => {
				return from(
					this.wsManagerService.subscribeToPublicChannel(PUBLIC_CHANNEL_PREFIXES.TASK_STATUS, taskId),
				).pipe(
					mergeMap((subId) => {
						return this.wsManagerService.getListener(subId);
					}),
					filter((res) => res && res.data),
					map((res) => res.data),
					filter((res) => res.taskId === taskId),
					filter((taskResponse) => taskResponse.status === "Completed" || taskResponse.status === "Stopped"),
					mergeMap((taskResponse: any) => {
						if (taskResponse.status === "Stopped") {
							this.state.next("runTask:error");
							return of(Error("Task stopped"));
						} else {
							this.state.next("fetchOutputs");
							return this.signaloidWrapperService.getTaskOutputs(
								taskResponse.taskId,
								this.authorizationFree,
							);
						}
					}),
					tap((taskResponse: any) => {
						console.log(taskResponse);
						if (taskResponse.status && taskResponse.status === "Completed") {
							this.state.next("idle");
						}
					}),
					catchError((err) => {
						return of({ error: err, authFree: true });
					}),
				);
			}),
		);
	}

	public async connectRepositoryIfNotConnected() {
		if (this.authorizationFree) {
			console.warn("Not connecting repository because authorizationFree is true");
			return;
		}
		const repo = this.repository;
		const connectedRepository = await this.signaloidWrapperService.getRepositoryByUrl(repo.RemoteURL);
		if (!connectedRepository) {
			this.state.next("connectRepo");
			const repo = await this.signaloidWrapperService.registerRepositoryToCurrentUser(this.repository);
			this.repository = repo;
			return repo;
		} else if (
			connectedRepository?.Core &&
			this.demoObject?.coreId &&
			connectedRepository.Core !== this.demoObject.coreId
		) {
			return await this.signaloidWrapperService.updateCoreInRepository(
				connectedRepository.RepositoryID,
				this.demoObject.coreId,
			);
		} else {
			this.repository = connectedRepository;
			return connectedRepository;
		}
	}

	fetchJsonFromUrl(url: string): Observable<any> {
		try {
			return this.http.get(url);
		} catch (error) {
			console.error(`Failed to fetch JSON from ${url}:`, error);
			throw error;
		}
	}

	protected loaderSteps: LoaderStep[] = [
		{ identifier: "connectRepo", label: "Connecting repository", status: "pending" },
		{ identifier: "building_repo", label: "Building repository", status: "pending" },
		{ identifier: "startTask", label: "Initiating task", status: "pending" },
		{ identifier: "runTask", label: "Running task", status: "pending" },
		{ identifier: "fetchOutputs", label: "Fetching task outputs", status: "pending" },
	];

	public getLoaderStepsBasedOnTaskState() {
		return this.state.pipe(
			map((state) => {
				const [step, error] = state.split(":");
				console.log(step, error);
				const stepIndex = this.loaderSteps.findIndex((elem) => elem.identifier === step);
				this.loaderSteps = this.loaderSteps.map((step, index) => {
					if (index < stepIndex) {
						return Object.assign({}, step, {
							status: "done",
						});
					} else if (index === stepIndex) {
						return Object.assign({}, step, {
							status: error ? "failed" : "inProgress",
						});
					} else if (index !== -1 && index > stepIndex) {
						return Object.assign({}, step, {
							status: "pending",
						});
					} else {
						return Object.assign({}, step, {
							status: "done",
						});
					}
				});
				return this.loaderSteps;
			}),
		);
	}

	private subscribeToTaskChannelWs(taskId: string) {
		if (this.authorizationFree)
			return from(this.wsManagerService.subscribeToPublicChannel(PUBLIC_CHANNEL_PREFIXES.TASK_STATUS, taskId));
		return from(this.wsManagerService.subscribeToChannel(AVAILABLE_CHANNEL_PREFIXES.TASK_STATUS));
	}

	private subscribeToBuildChannelWs() {
		return from(this.wsManagerService.subscribeToChannel(AVAILABLE_CHANNEL_PREFIXES.BUILD_STATUS));
	}
	public listFilesFromStorage() {
		return this.signaloidWrapperService.listFiles().pipe(
			take(1),
			map((files) => files.items),
		);
	}

	public uploadFilesNeededForDemo(necessaryFilesForDemo: string[]) {
		return new Promise((resolve, reject) => {
			this.listFilesFromStorage()
				.pipe(
					take(1),
					tap((existingFiles) => {
						const missingFiles = necessaryFilesForDemo.filter(
							(fp) => !existingFiles.map((entry) => entry.path).includes(fp),
						);
						if (missingFiles.length === 0) {
							resolve(true);
							return;
						}
						missingFiles.forEach((file) => {
							this.http
								.get(`/demos/csvs/${file}`, { responseType: "blob" })
								.pipe(
									take(1),
									mergeMap((blob) => {
										return this.signaloidWrapperService.getFileLocationToUpload(`${file}`, blob);
									}),
									catchError((err) => {
										console.log(err);
										this.snackbarService.openSnackbar({
											header: `Error uploading file, Please try again later`,
											type: "error",
											description: makeErrorReadable(err),
										});

										reject(err);
										return of(null);
									}),
								)
								.subscribe((res) => {
									missingFiles.splice(missingFiles.indexOf(file), 1);
									if (missingFiles.length === 0) {
										resolve(res);
									}
								});
						});
					}),
				)
				.subscribe();
		});
	}

	public clearDemo() {
		this.demoObject = undefined;
		this.arguments = "";
		this.defaultArguments = "";
		this.buildId = "";
		this.argumentsMap = {};
		this.repository = undefined;
	}

	public clearDemoPromise(): Promise<boolean> {
		return new Promise((resolve) => {
			this.wsManagerService.unsubscribeAllPublicChannels();
			this.snackbarService.removeSnackbar();
			this.state = new BehaviorSubject("idle");
			this.demoObject = undefined;
			this.arguments = "";
			this.defaultArguments = "";
			this.buildId = "";
			this.argumentsMap = {};
			this.repository = undefined;
			resolve(true);
		});
	}
}
